import { useEffect, type JSX } from "react";
import type { PromotedCharacter } from "./unlockTransitionTracker";

export type UnlockToastProps = {
  /** 昇格対象のキャラ一覧。空配列のときは何も描画しない (Req 4.4 session-local 扱いとも整合)。 */
  promotedCharacters: PromotedCharacter[];
  /** 自動 / 手動の dismiss 共通コールバック。3 秒経過で自動的に呼び出す (Req 4.2)。 */
  onDismiss: () => void;
};

// 自動 dismiss までの時間 (ms)。Req 4.2「一定時間 (3 秒程度) で自動的に消える」。
const AUTO_DISMISS_MS = 3000;

/**
 * character-unlock spec の最小 toast (Task 3.3)。
 *
 * - promotedCharacters が空のときは `return null` (DOM ゼロ)
 * - mount 時に setTimeout で 3 秒後に onDismiss を呼ぶ (Req 4.2)
 * - 単一昇格は「{name} の一面が解放されました」、複数昇格は「N 人の一面が解放されました」と集約 (Req 4.1, 4.3)
 * - aria-live="polite" + pointer-events-none で reader 操作・本文閲覧を阻害しない (Req 4.5)
 * - portal 不要: PlayerScreen 親に対して fixed bottom-4 で十分配置可能、CharacterCodexView の z-50 と独立した z-40
 *
 * 通知履歴は本 component では持たない (Req 4.4 session-local、永続化なし)。
 * promotedCharacters 配列の入れ替え (新たな昇格発生) は親が key 変更などで unmount/remount する想定。
 */
export function UnlockToast({
  promotedCharacters,
  onDismiss,
}: UnlockToastProps): JSX.Element | null {
  // 空配列のとき render 経由で何も DOM 出力しないが、
  // React Hooks ルール上 effect は早期 return より前に置く必要がある。
  // そのため effect 内で空配列を弾いて timer を起動しない。
  const hasPromotion = promotedCharacters.length > 0;
  useEffect(() => {
    if (!hasPromotion) return;
    const timerId = setTimeout(() => {
      onDismiss();
    }, AUTO_DISMISS_MS);
    return () => clearTimeout(timerId);
  }, [hasPromotion, onDismiss]);

  if (!hasPromotion) return null;

  // 集約表示: 1 件は名前明記、2 件以上は人数のみ。
  // 個別に列挙すると長文化して本流コンテンツを圧迫するため (Req 4.3)。
  const message =
    promotedCharacters.length === 1
      ? `${promotedCharacters[0]!.name} の一面が解放されました`
      : `${promotedCharacters.length} 人の一面が解放されました`;

  return (
    <div
      data-testid="unlock-toast"
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-amber-700/90 text-white px-4 py-2 rounded shadow-lg pointer-events-none"
    >
      {message}
    </div>
  );
}
